import React, { useCallback, useEffect, useRef } from 'react';
import { X, AlertCircle, AlertTriangle, Link } from 'lucide-react';
import FeatureConfirmationDialog from './FeatureConfirmationDialog';
import DependencyReviewComponent from './DependencyReviewComponent';
import { useFeatureNavigation } from '../services/featureNavigation';
import DialogNavigationWrapper from './DialogNavigationWrapper';
import WelcomeDialog from './WelcomeDialog';
import debugLogger from '../utils/debug';

const DialogManager = (props) => {
    const {
        onResponseChange,
        dialogQueue,
        setDialogQueue,
        pendingResponse,
        setPendingResponse,
        pendingResponseRef,
        updateDependencyTracking,
        onNavigateToFeature
    } = props;

    const isProcessing = useRef(false);
    const isNavigating = useRef(false);
    const currentDialog = dialogQueue?.[0];
    const { navigateToFeature } = useFeatureNavigation();

    // Feature context tracking
    const featureContextRef = useRef(null);

    // Handle dialog confirmation with improved feature reference tracking
    const handleConfirm = useCallback(async () => {
        if (!currentDialog || !pendingResponse || isProcessing.current) return;
    
        isProcessing.current = true;
        try {
            const { type } = currentDialog;
            const { featureRef, value } = pendingResponse;
    
            switch (type) {
                case 'warning':
                    // Remove warning dialog
                    setDialogQueue(prev => prev.slice(1));
                    
                    // If there are no more dialogs, process the response
                    if (dialogQueue.length <= 1) {
                        debugLogger.debug('DIALOG', 'Processing response after warning', { featureRef, value });
                        await onResponseChange(featureRef, value, true);
                        if (updateDependencyTracking) {
                            await updateDependencyTracking();
                        }
                        // Clear state after processing
                        setPendingResponse(null);
                        pendingResponseRef.current = null;
                    }
                    // If there are more dialogs, preserve the pending response
                    return;
                    
                case 'dependency':
                    // Process main feature - backend handles system dependencies
                    await onResponseChange(featureRef, value, true);
                    if (updateDependencyTracking) {
                        await updateDependencyTracking();
                    }
                    break;

                case 'dependency-review':
                    debugLogger.debug('DIALOG', 'Processing dependency review', {
                        feature: currentDialog.feature,
                        dependencies: currentDialog.dependencies,
                        featureRef: featureRef
                    });

                    // Cache the target values
                    const targetFeatureRef = currentDialog.feature?.reference || featureRef;
                    const targetValue = value;
                    const systemDependencies = currentDialog.dependencies?.filter(dep => dep.is_system_dependency) || [];

                    // Process all system dependencies first
                    for (const dep of systemDependencies) {
                        debugLogger.debug('DIALOG', 'Clearing system dependency', dep.feature_reference);
                        await onResponseChange(dep.feature_reference, null, true);
                    }

                    // Process main feature
                    debugLogger.debug('DIALOG', 'Unsetting main feature', targetFeatureRef);
                    await onResponseChange(targetFeatureRef, targetValue, true);

                    // Only update dependencies after successful unset
                    if (updateDependencyTracking) {
                        await updateDependencyTracking();
                    }
                    break;

                case 'conflict':
                    debugLogger.debug('DIALOG', 'Processing conflict, clearing state');
                    break;

                case 'welcome':
                    // Simply close the dialog
                    setDialogQueue([]);
                    break;

                default:
                    debugLogger.debug('DIALOG', 'Processing default case');
                    break;
            }

            // Clear everything after processing
            setDialogQueue([]);
            setPendingResponse(null);
            pendingResponseRef.current = null;
            featureContextRef.current = null;

        } catch (error) {
            debugLogger.error('DIALOG', 'Dialog confirmation failed', error);
        } finally {
            isProcessing.current = false;
        }
    }, [currentDialog, pendingResponse, onResponseChange, setDialogQueue, setPendingResponse, updateDependencyTracking, dialogQueue.length]);

    const handleClose = useCallback(() => {
        if (isProcessing.current) return;
        
        debugLogger.debug('DIALOG', 'Closing dialog, clearing state');
        setDialogQueue([]);
        setPendingResponse?.(null);
        pendingResponseRef.current = null;
        featureContextRef.current = null;
    }, [setDialogQueue, setPendingResponse]);

    const handleNavigateToFeature = useCallback(async (targetFeatureRef, sourceFeatureRef) => {
        if (isNavigating.current) return;
        
        debugLogger.debug('DIALOG', 'Starting feature navigation', {
            target: targetFeatureRef,
            source: sourceFeatureRef,
            currentFeature: featureContextRef.current?.reference
        });

        isNavigating.current = true;
        try {
            await navigateToFeature(targetFeatureRef);
            if (onNavigateToFeature) {
                onNavigateToFeature(targetFeatureRef);
            }
        } catch (error) {
            debugLogger.error('DIALOG', 'Navigation failed', error);
        } finally {
            isNavigating.current = false;
        }
    }, [navigateToFeature, onNavigateToFeature]);
    
    if (!currentDialog) return null;

    // Prevent rendering during processing
    if (isProcessing.current) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white p-4 rounded-lg shadow-lg">Processing...</div>
            </div>
        );
    }

    // Special handling for welcome dialog
    if (currentDialog.type === 'welcome') {
        return (
            <DialogNavigationWrapper onClose={handleClose}>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold">Welcome to C360</h2>
                        <button 
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-100 rounded-full"
                        >
                            <X className="w-5 h-5 text-gray-400" />
                        </button>
                    </div>
                    <div className="space-y-6">
                        <WelcomeDialog onClose={handleClose} />
                    </div>
                </div>
            </DialogNavigationWrapper>
        );
    }

    // Render appropriate dialog type
    return currentDialog.type === 'dependency-review' ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <DependencyReviewComponent
                feature={currentDialog.feature}
                dependencies={currentDialog.dependencies}
                onNavigateToFeature={handleNavigateToFeature}
                onConfirm={handleConfirm}
                onClose={handleClose}
                isNavigating={isNavigating.current}
            />
        </div>
    ) : (
        <FeatureConfirmationDialog
            isOpen={true}
            onClose={handleClose}
            onConfirm={handleConfirm}
            type={currentDialog.type}
            feature={currentDialog.feature}
            message={currentDialog.message}
            dependencies={currentDialog.dependencies || []}
            conflicts={currentDialog.conflicts || []}
            onNavigateToFeature={handleNavigateToFeature}
            isNavigating={isNavigating.current}
        />
    );
};

export default DialogManager;